import type { Repo } from '../store/repo';
import { openCoteDB } from '../store/db';
import { Repo as RepoClass } from '../store/repo';
import { SyncEngine, type SearchBox, type SyncStatus } from '../sync/sync';
import { OcrScheduler } from '../sync/ocr';
import type { Notebook } from '../store/types';
import type { Theme } from '../theme/tokens';
import type { ClipboardPayload } from '../ink/engine';

export type Route =
  | { screen: 'library' }
  | { screen: 'editor'; notebookId: string; pageId: string }
  | { screen: 'trash' };

/** One search result row, resolved against local IDB (M5 spec §3.2). */
export interface SearchHit {
  pageId: string;
  kind: 'ocr' | 'text';
  /** Raw snippet with literal `**…**` markers — escape HTML before bolding. */
  snippet: string;
  /** OCR word rects (world coords) matching the query; ocr rows only. */
  boxes?: SearchBox[];
  /** null when the page/notebook is unknown locally (the "Other" group). */
  notebookId: string | null;
  /** 1-based position among the notebook's LIVE pages; null when unknown. */
  pageNumber: number | null;
}

/** Search hits grouped by notebook; `notebookId: null` = the "Other" bucket. */
export interface SearchGroup {
  notebookId: string | null;
  title: string;
  hits: SearchHit[];
}

class AppState {
  route = $state<Route>({ screen: 'library' });
  theme = $state<Theme>('light');
  themeOverride = $state<Theme | undefined>(undefined);
  repo: Repo | null = $state(null);
  toast = $state<{ message: string; undo?: () => Promise<void> } | null>(null);
  /** 'disabled' when sync is off; otherwise mirrors the engine's status. */
  syncStatus = $state<SyncStatus | 'disabled'>('disabled');
  /** Reactive so the Editor can (re)subscribe to pagechanged when sync starts. */
  syncEngine: SyncEngine | null = $state(null);
  /** In-memory stroke clipboard (M3a lasso copy/paste): survives page navigation, not reload. */
  clipboard = $state<ClipboardPayload | null>(null);
  /**
   * One-shot handoff from a tapped search result to the Editor (M5 spec §3.2):
   * the Editor consumes (nulls) it when it mounts the matching page and draws
   * the match-highlight overlay from the world-coord boxes.
   */
  pendingSearchHighlight = $state<{ pageId: string; boxes: SearchBox[] } | null>(null);
  /** OCR scheduler (M5): lives exactly as long as the sync engine it feeds. */
  private ocrScheduler: OcrScheduler | null = null;

  async init() {
    this.repo = new RepoClass(await openCoteDB());
    const settings = await this.repo.getSettings();
    this.themeOverride = settings.themeOverride;
    this.applyTheme();
    matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => this.applyTheme());
    void this.checkStorage();
    if (await this.repo.getSyncMeta('syncEnabled')) this.startSyncEngine();
  }

  /** Poll interval: `?syncPoll=<ms>` override (E2E tests), else 15s. */
  private syncPollMs(): number {
    const p = Number(new URLSearchParams(location.search).get('syncPoll'));
    return Number.isFinite(p) && p > 0 ? p : 15_000;
  }

  /** OCR settle window: `?ocrDebounce=<ms>` override (E2E tests), else 30s. */
  private ocrDebounceMs(): number {
    const p = Number(new URLSearchParams(location.search).get('ocrDebounce'));
    return Number.isFinite(p) && p > 0 ? p : 30_000;
  }

  private startSyncEngine() {
    const engine = new SyncEngine(this.repo!, { pollMs: this.syncPollMs() });
    // Guard: an in-flight sync can emit status after this engine is stopped
    // and replaced/disabled — ignore events from anything but the live engine.
    engine.on('status', (s) => {
      if (this.syncEngine === engine) this.syncStatus = s;
    });
    this.syncStatus = engine.status;
    this.syncEngine = engine;
    void engine.start();
    // OCR rides the engine's push cycle (M5): same lifetime as the engine.
    this.ocrScheduler = new OcrScheduler(
      this.repo!,
      { post: (pageId, rev, scale, png) => engine.postOcr(pageId, rev, scale, png) },
      { debounceMs: this.ocrDebounceMs() },
    );
    this.ocrScheduler.start(engine);
  }

  private stopSyncEngine() {
    this.ocrScheduler?.stop();
    this.ocrScheduler = null;
    this.syncEngine?.stop();
    this.syncEngine = null;
  }

  /** Store the token, journal all existing local data, and start syncing. */
  async enableSync(token: string) {
    // A token change requires a fresh engine (it reads the token at start()).
    this.stopSyncEngine();
    await this.repo!.setSyncMeta('syncToken', token);
    await this.repo!.setSyncMeta('syncEnabled', true);
    await this.repo!.enqueueFullExport(); // spec §3.4: first-enable full export
    this.startSyncEngine();
  }

  /** Stop syncing; the token stays stored so re-enabling is one tap. */
  async disableSync() {
    this.stopSyncEngine();
    await this.repo!.setSyncMeta('syncEnabled', false);
    this.syncStatus = 'disabled';
  }

  /**
   * Library search (M5 spec §3.2): server FTS via the engine's authed fetch,
   * then group rows by notebook resolved from local IDB. Rows whose page or
   * notebook is unknown locally (not yet synced, tombstoned) land in a single
   * trailing "Other" group with no page number. Group order follows the best
   * (first-seen) rank of each notebook; hit order inside a group is rank order.
   */
  async search(q: string): Promise<SearchGroup[]> {
    const engine = this.syncEngine;
    if (!engine) throw new Error('sync disabled: search requires sync enabled');
    const { results } = await engine.search(q);
    const repo = this.repo!;

    // Memoized per-notebook live page numbering (pageOrder minus tombstones).
    const numbering = new Map<string, Map<string, number>>();
    const liveNumbers = async (nb: Notebook): Promise<Map<string, number>> => {
      let m = numbering.get(nb.id);
      if (!m) {
        m = new Map();
        let n = 0;
        for (const pid of nb.pageOrder) {
          const p = await repo.getPage(pid);
          if (p && !p.deletedAt) m.set(pid, ++n);
        }
        numbering.set(nb.id, m);
      }
      return m;
    };

    const notebooks = new Map<string, Notebook | null>(); // null = unknown/tombstoned
    const groups = new Map<string, SearchGroup>(); // key '' = "Other"
    for (const row of results) {
      let notebook: Notebook | null = null;
      let pageNumber: number | null = null;
      const page = await repo.getPage(row.pageId);
      if (page && !page.deletedAt) {
        let nb = notebooks.get(page.notebookId);
        if (nb === undefined) {
          const rec = await repo.getNotebook(page.notebookId);
          nb = rec && !rec.deletedAt ? rec : null;
          notebooks.set(page.notebookId, nb);
        }
        if (nb) {
          notebook = nb;
          pageNumber = (await liveNumbers(nb)).get(row.pageId) ?? null;
        }
      }
      const key = notebook?.id ?? '';
      let group = groups.get(key);
      if (!group) {
        group = { notebookId: notebook?.id ?? null, title: notebook?.title ?? 'Other', hits: [] };
        groups.set(key, group);
      }
      group.hits.push({
        pageId: row.pageId,
        kind: row.kind,
        snippet: row.snippet,
        boxes: row.boxes,
        notebookId: notebook?.id ?? null,
        pageNumber,
      });
    }
    // "Other" (unresolvable) sinks below every real notebook group.
    const list = [...groups.values()];
    const otherIdx = list.findIndex((g) => g.notebookId === null);
    if (otherIdx !== -1) list.push(...list.splice(otherIdx, 1));
    return list;
  }

  applyTheme() {
    const system: Theme = matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    this.theme = this.themeOverride ?? system;
    document.documentElement.classList.toggle('dark', this.theme === 'dark');
  }

  async setThemeOverride(t: Theme | undefined) {
    this.themeOverride = t;
    await this.repo?.patchSettings({ themeOverride: t });
    this.applyTheme();
  }

  showToast(message: string, undo?: () => Promise<void>) {
    this.toast = { message, undo };
    setTimeout(() => { if (this.toast?.message === message) this.toast = null; }, 6000);
  }

  /**
   * Store a file (M4 spec §1): upload to R2 via POST /api/files (bearer token
   * from syncmeta, same-origin) and ALWAYS cache the bytes locally with
   * repo.putFile so local access never needs the network. When sync is
   * disabled the POST is skipped and a locally-minted `local-<uuid>` id is
   * returned — the file exists only on this device, and enabling sync later
   * does NOT retro-upload it (v1 limitation). The CALLER is responsible for
   * warning the user in that case (toast).
   */
  async uploadFile(bytes: ArrayBuffer, name: string, type: string): Promise<string> {
    let fileId: string;
    if (this.syncStatus === 'disabled') {
      fileId = `local-${crypto.randomUUID()}`;
    } else {
      const token = (await this.repo!.getSyncMeta('syncToken')) as string | undefined;
      if (!token) throw new Error('no sync token stored');
      const res = await fetch(`/api/files?name=${encodeURIComponent(name)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': type || 'application/octet-stream' },
        body: bytes,
      });
      if (!res.ok) throw new Error(`upload failed (${res.status})`);
      fileId = ((await res.json()) as { fileId: string }).fileId;
    }
    await this.repo!.putFile(fileId, bytes);
    return fileId;
  }

  /** Warn (non-modal) when browser storage is nearly full. */
  async checkStorage() {
    try {
      const est = await navigator.storage?.estimate?.();
      if (est?.usage && est?.quota && est.usage / est.quota > 0.8) {
        this.showToast('Storage is nearly full — notes may stop saving.');
      }
    } catch { /* unsupported: ignore */ }
  }
}

export const app = new AppState();
